import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import { getCompanyApi } from '../../../components/api'
import { ICompany } from '../../../components/models/models'
import { IBankModelList } from '../models/models'
import { GetBanksApi } from '../services/getBanksApi'
import Table from '../../../../../components/Table'
import { ListPosDataApi } from '../../PosCompany/service/listPosDataApi'
import { IPosData } from '../../PosCompany/models/models'

interface IListInstallmentState {
  company?: ISelectOption
  pos?: ISelectOption
  type?: ISelectOption | null
}

function BankList(props: BaseComponentWithContextProps) {
  const normalizeText = (name?: string) =>
    (name || '')
      .toLocaleUpperCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

  const getInitialState = (): IListInstallmentState => {
    return {
    }
  }
  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<IListInstallmentState>(intialState)
  const [companies, setCompanies] = useState<ISelectOption[]>([])
  const [bank, setBank] = useState<IBankModelList[]>([])
  const [filterBank, setFilterBank] = useState<IBankModelList[]>([])
  const [posDataList, setPosDataList] = useState<IPosData[]>([])
  const [posFilterOptions, setPosFilterOptions] = useState<ISelectOption[]>([])

  useEffect(() => {
    getCompanies()
    getPosData()
    getBanks()
  }, [])

  const getPosData = async () => {
    try {
      const res = await new ListPosDataApi({}).listPosData()
      setPosDataList(res || [])
    } catch (e: any) {
      Swal.fire({
        icon: 'error',
        title: e.description,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  const getCompanies = async () => {
    const company: ICompany[] = await getCompanyApi()
    const data: ISelectOption[] = []
    company
      .filter(c => {
        return c.Name !== 'Index Grup'
      })
      .map(com => {
        if (com.Name === 'Index') data.push({ value: '1000', label: com.Name })
        if (com.Name === 'Datagate') data.push({ value: '1100', label: com.Name })
        if (com.Name === 'Despec') data.push({ value: '1200', label: com.Name })
        if (com.Name === 'Netex') data.push({ value: '2300', label: com.Name })
      })
    setCompanies(data.sort((a, b) => Number(a.value) - Number(b.value)))
  }

  const getBanks = async () => {
    props.showLoading()
    const bankApi: GetBanksApi = new GetBanksApi({})
    bankApi.listAllBanks().then((res: any) => {
      if (res) {
        setBank(res)
        setFilterBank(res)
        buildPosFilters(res, posDataList)
        props.hideLoading()
      }
    })
      .catch((e: any) => {
        Swal.fire({
          icon: 'error',
          title: e.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        props.hideLoading()
      })
  }

  const buildPosFilters = (banks: IBankModelList[], posData: IPosData[]) => {
    const flagToName: Record<number, string> = {}

    // Önce API'den gelen POS isimlerini, bank kayıtlarında geçen flag'lerle eşleştir.
    banks.forEach(b => {
      const joined = normalizeText(`${b.Poscode || ''} ${b.TheName || ''}`)
      posData.forEach(p => {
        const pName = normalizeText(p.Name)
        if (joined.includes(pName)) {
          if (!flagToName[Number(b.Flags)]) {
            flagToName[Number(b.Flags)] = p.Name
          }
        }
      })
    })

    // Kayıtlarda flag=1 varsa Normal POS'u ekle.
    if (banks.some(b => Number(b.Flags) === 1)) {
      flagToName[1] = flagToName[1] || 'Normal POS'
    }

    const options = Object.keys(flagToName)
      .map(flag => ({ value: Number(flag), label: flagToName[Number(flag)] }))
      .sort((a, b) => Number(a.value) - Number(b.value))

    setPosFilterOptions(options)
  }

  useEffect(() => {
    if (bank.length > 0) {
      buildPosFilters(bank, posDataList)
    }
  }, [posDataList, bank])

  const tableColumns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'index',
      },
      {
        Header: 'Şirket',
        accessor: 'company',
      },
      {
        Header: 'Banka',
        accessor: 'banks',
      },
      {
        Header: 'Durum',
        accessor: 'active',
      },
      {
        Header: 'Flags',
        accessor: 'flags',
      },
      {
        Header: 'BankID',
        accessor: 'bankID',
      },
      {
        Header: 'PosType',
        accessor: 'posType',
      },
      {
        Header: 'PosCode',
        accessor: 'posCode',
      },
    ],
    []
  )

  const tableData = useMemo(() =>
    filterBank.map((element, index) => {
      const posName = posFilterOptions.find(p => Number(p.value) === Number(element.Flags))?.label
      const data = {
        index: index + 1,
        company: companies.find(a => element.Company === a.value)?.label,
        banks: element.TheName,
        active: element.Active === 1 ? "Aktif" : "Pasif",
        flags: `${element.Flags}${posName ? ` (${posName})` : ''}`,
        bankID: element.BankId,
        posType: element.Vpostype,
        posCode: element.Poscode
      }
      return data
    })
    , [filterBank, companies, posFilterOptions])

  useEffect(() => {
    let filtered = bank
    if (state.company) {
      filtered = filtered.filter(a => a.Company === state.company?.value)
    }
    if (state.pos) {
      filtered = filtered.filter(a => Number(a.Flags) === Number(state.pos?.value))
    }
    setFilterBank(filtered)
  }, [state.company, state.pos, bank, posDataList])

  return (
    <div className='container'>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <div className='col-12 col-md-9 mb-2'>
              <span className='h4'>Banka Listesi</span>
            </div>
          </div>
          <div className='card-body'>
            <div className='form-row'>
              <div className='form-group col-md-3'>
                <label className='labeltext'>Şirket</label>
                <IPSelectBox
                  isClearable
                  options={companies}
                  value={state.company}
                  onChangeSingle={option => {
                    setState({ company: option })
                  }}
                />
              </div>
              <div className='form-group col-md-3'>
                <label className='labeltext'>POS</label>
                <IPSelectBox
                  isClearable
                  options={posFilterOptions}
                  value={state.pos}
                  onChangeSingle={option => {
                    setState({ pos: option })
                  }}
                />
              </div>
            </div>
            <div className='form-row'>
              <Table columns={tableColumns} data={tableData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default componentWithContext(BankList)

